// src/pages/Assistant/components/renderLoadingItems.tsx
import React, { useState } from "react";
import type { LoadingItem } from "../utils/useLoadingStream";
import { MdArrowDropDown, MdArrowDropUp } from "react-icons/md";
import { TiArrowSortedDown, TiArrowSortedUp } from "react-icons/ti";

function labelFor(type: LoadingItem["type"]) {
  switch (type) {
    case "thinking":
      return "Thinking";
    case "reflecting":
      return "Reflecting";
    case "step":
      return "Step";
    case "code_generation_start":
      return "Generating code";
    case "code_execution_start":
      return "Running code";
    case "generated_plots_processing":
      return "Processing plots";
    default:
      return "Working";
  }
}

type PanelProps = { item: LoadingItem };

// --- grapheme splitter ---
function getFirstGrapheme(str: string): { first: string; rest: string } {
  if (!str) return { first: "", rest: "" };

  try {
    const seg = new Intl.Segmenter(undefined, { granularity: "grapheme" });
    const parts = Array.from(seg.segment(str), (s) => s.segment);
    return { first: parts[0] ?? "", rest: parts.slice(1).join("") };
  } catch {
    // fallback (less accurate, but avoids crashes)
    const chars = Array.from(str);
    return { first: chars[0] ?? "", rest: chars.slice(1).join("") };
  }
}

function renderContentWithEmoji(item: LoadingItem) {
  if (!item?.content) return null;

  const { first, rest } = getFirstGrapheme(item.content);

  // if first char is NOT a-zA-Z0-9, treat it as emoji/symbol
  const isEmojiOrSymbol = !/^[a-zA-Z0-9]$/.test(first);

  if (isEmojiOrSymbol) {
    return (
      <div className="leading-relaxed overflow-y-auto max-h-40 flex">
        <span className="mr-1">{first}</span>
        <span className="pulse-text-left-to-right whitespace-pre-wrap">
          {rest}
          <span className="loading-dots ml-1 w-[10px]" aria-hidden="true" />
        </span>
      </div>
    );
  }

  return (
    <div className="leading-relaxed overflow-y-auto max-h-40">
      <span className="pulse-text-left-to-right whitespace-pre-wrap">
        {item.content}
        <span className="loading-dots ml-1 w-[10px]" aria-hidden="true" />
      </span>
    </div>
  );
}

export function ThinkingPanel({ item }: PanelProps) {
  if (!item) return null;
  return (
    <div className="bg-blue-50 rounded-lg p-3 max-w-[100%] border border-blue-500">
      <div className="text-sm">
        <div className="font-medium text-blue-600 mb-1">
          {labelFor(item.type)}
        </div>
        {renderContentWithEmoji(item)}
      </div>
    </div>
  );
}

export function StepsPanel({ item }: PanelProps) {
  if (!item) return null;
  const showHeader = item.type !== "step";
  return (
    <div className="bg-[#F3F4F6] rounded-lg p-3 max-w-[100%]">
      <div className="text-sm">
        {showHeader && (
          <div className="font-medium text-blue-600 mb-1">
            {labelFor(item.type)}
          </div>
        )}
        {renderContentWithEmoji(item)}
      </div>
    </div>
  );
}

export function renderLoadingItems(items: LoadingItem[]): React.ReactNode {
  if (!items?.length) return null;
  const it = items[0];
  return it.type === "thinking" ? (
    <ThinkingPanel item={it} />
  ) : (
    <StepsPanel item={it} />
  );
}

export const IntermediatePanel = ({ item }: { item: { content: string } }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-blue-50 rounded-lg p-3 max-w-[100%] border border-blue-500">
      <div
        className="cursor-pointer text-sm font-medium text-blue-600 flex justify-between items-center h-6"
        onClick={() => setExpanded(!expanded)}
      >
        <span>Thinking Trace</span>
        <span className="text-gray-400">
          {expanded ? (
            <TiArrowSortedUp className="w-6 h-6" />
          ) : (
            <TiArrowSortedDown className="w-6 h-6" />
          )}
        </span>
      </div>
      {expanded && (
        <div className="mt-2 text-gray-700 text-sm whitespace-pre-wrap">
          {item.content}
        </div>
      )}
    </div>
  );
};
